import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteWorkspace } from '@/lib/api/generated/api';
import { WorkspaceDeleteError } from '../errors';
import { workspaceKeys } from '../queryKeys';

/**
 * Permanently deletes a workspace and everything it contains.
 *
 * This does not go through `expectStatus`: a caller has to tell three failures
 * apart — the service refused because work is running (409), someone else
 * already deleted it (404), and the request never happened — and collapsing
 * them into one generic `Error` would leave the dialog guessing from message
 * text. Each outcome becomes a `WorkspaceDeleteError` with its own `kind`.
 *
 * Success invalidates the list (the service owns membership and ordering; no
 * local splicing) and drops the workspace's cached aggregate. "Already gone"
 * also refreshes the list — the row it describes should stop being shown,
 * whoever deleted it.
 */
export function useDeleteWorkspace() {
  const queryClient = useQueryClient();

  const refreshList = () =>
    queryClient.invalidateQueries({ queryKey: workspaceKeys.list() });

  return useMutation({
    mutationFn: async (workspaceId: string): Promise<void> => {
      let status: number;
      try {
        status = (await deleteWorkspace(workspaceId)).status;
      } catch {
        throw new WorkspaceDeleteError(
          'unavailable',
          'The deletion could not be performed. Check your connection and try again.',
        );
      }
      if (status === 204) return;
      if (status === 409) {
        throw new WorkspaceDeleteError(
          'active-execution',
          'This workspace has a task still running. Wait for it to finish, then try again.',
        );
      }
      if (status === 404) {
        throw new WorkspaceDeleteError(
          'not-found',
          'This workspace was already deleted. It has been removed from the list.',
        );
      }
      throw new WorkspaceDeleteError(
        'unavailable',
        'The deletion could not be performed. Try again.',
      );
    },
    onSuccess: (_data, workspaceId) => {
      queryClient.removeQueries({
        queryKey: workspaceKeys.detail(workspaceId),
      });
      void refreshList();
    },
    onError: (error) => {
      if (error instanceof WorkspaceDeleteError && error.kind === 'not-found') {
        void refreshList();
      }
    },
  });
}
